import {
  useMyHealthReports,
  type HealthReportPublicationMeDto,
} from '@/api/useMyHealthReports';
import { Card, EmptyState, ErrorBanner, PageHeader, StageBadge } from '@/components/AppShell';
import { formatDateTime } from '@/utils/formatDate';

/**
 * 已审核健康报告页（Issue #198）。
 *
 * 红线：
 *   - 只展示后端 published 行；hook 内部已二次过滤为 state === 'published'。
 *   - 不展示 reviewedBy / reviewNote / sourceProposalReviewId 等内部字段。
 *   - 客户端不展示 AI 草稿；显示提示告知"AI 模拟仅在医生发布后可见"。
 *   - 指标值直接展示后端下发字符串，不做客户端单位换算或风险阈值推断。
 */
const REPORT_KIND_LABEL: Record<
  HealthReportPublicationMeDto['reportKind'],
  string
> = {
  health_trend: '健康趋势',
  health_report_summary: '健康报告摘要',
  ai_prognosis_summary: 'AI 预后模拟（已审核）',
};

export function HealthReportsPage() {
  const { publications, loading, error, refetch } = useMyHealthReports();

  return (
    <div>
      <PageHeader
        title="已审核报告"
        description="仅展示经医生或技术负责人审核并发布的报告。AI 草稿不会在此显示。"
      />
      {error ? <ErrorBanner message={error.message} onRetry={refetch} /> : null}

      {loading ? (
        <Card>
          <p className="text-sm text-muted-foreground">加载报告中…</p>
        </Card>
      ) : publications.length === 0 ? (
        <Card>
          <EmptyState
            title="暂无已发布报告"
            description="待医生发布健康趋势、报告摘要或 AI 预后模拟后再返回查看。"
          />
        </Card>
      ) : (
        <ul className="space-y-3">
          {publications.map((pub) => (
            <li key={pub.id}>
              <ReportCard pub={pub} />
            </li>
          ))}
        </ul>
      )}

      <Card className="mt-4 border-dashed">
        <p className="text-xs text-muted-foreground">
          AI 预后模拟基于历史与公开数据训练，仅作为辅助参考，不构成诊断或治疗承诺。
          如有疑问，请联系您的医生与专属顾问。
        </p>
      </Card>
    </div>
  );
}

function ReportCard({ pub }: { pub: HealthReportPublicationMeDto }) {
  return (
    <Card className="space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{pub.title}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {REPORT_KIND_LABEL[pub.reportKind] ?? pub.reportKind}
          </p>
        </div>
        <StageBadge label="已发布" tone="good" />
      </div>
      {pub.summary ? <p className="text-sm">{pub.summary}</p> : null}
      {pub.metrics.length > 0 ? (
        <dl className="grid grid-cols-2 gap-2 rounded-md bg-muted/40 p-2 text-xs">
          {[...pub.metrics]
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((metric) => (
              <div
                key={`${pub.id}-${metric.metricCode}-${metric.recordedAt}`}
                className="flex flex-col"
              >
                <dt className="text-muted-foreground">{metric.metricLabel}</dt>
                <dd className="font-medium">
                  {metric.valueDecimal}
                  {metric.unit ? ` ${metric.unit}` : ''}
                </dd>
              </div>
            ))}
        </dl>
      ) : null}
      <p className="text-xs text-muted-foreground">
        发布时间 {formatDateTime(pub.publishedAt)}
      </p>
    </Card>
  );
}
